import {WorksitesMapProps} from "./types";
import {ScatterMap} from "@components/Maps/ScatterMap";
import React, {useCallback, useContext, useEffect, useState} from "react";
import AuthenticationService from "@services/AuthenticationService";
import {useIntl} from "react-intl";
import {useHomeWorksites} from "@feature/worksites/home/hooks/useHomeWorksites";
import {WorksitesPin} from "@feature/worksites/home/types";
import {BlurredPlaceholder} from "@feature/commons/blurred-placeholder";
import CountryContext from "../../../../CountryContext";
import {getBoundingBox} from "./lib/utils";
import {DEFAULT_VIEWPORT} from "@feature/worksites/home/map/lib/consts";
import _ from "lodash";
import {SUPPORTED_BUSINESSES, SUPPORTED_CATEGORIES} from "@components/Maps/ScatterMap/lib/consts";


export const WorksitesMap = ({filters, onClick, refreshObservable}: WorksitesMapProps) => {

  // context
  const {country: {code}} = useContext(CountryContext);

  // hooks
  const intl = useIntl();
  const {loading, fetchPins: fetch} = useHomeWorksites();

  // state
  const [data, setData] = useState<WorksitesPin[]>();
  const [boundingBox, setBoundingBox]
    = useState<{rng_latitude: [number, number], rng_longitude: [number, number]}>(
      getBoundingBox(
        [DEFAULT_VIEWPORT[code].longitude, DEFAULT_VIEWPORT[code].latitude],
        DEFAULT_VIEWPORT[code].zoom,
        0,
        0,
      )
    );
  const [deactivatedCategories, setDeactivatedCategories]
    = useState<Set<string>>(new Set());

  // utils
  const userData = AuthenticationService.getUserData();
  const LegendItem = ({color, text, onClick, active}: {
    color: string, text: string, onClick: () => void, active: boolean
  }) => (
    <div className="flex flex-row gap-2 items-center select-none cursor-pointer" onClick={onClick}>
      <div className={`rounded-full w-4 h-4 ${color} shadow-sm`} />
      <span className={`leading-tight text-xs ${!active ? "opacity-30" : ""}`}>{text}</span>
    </div>
  )
  const debouncedFetch = useCallback(
    _.debounce((filters, boundingBox, setData) => {
      fetch(filters, boundingBox).then(setData);
    }, 500),
    []  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const isKnaufVisibile = userData.groups.find((e: any) => e === "Knauf");
  const pinColors = {
    kerakoll: {
      created_not_shared: "bg-am-700",
      created_shared: "bg-kerakoll-800",
      visible: "bg-kerakoll-400",
    },
    knauf: {
      created_not_shared: "bg-knauf-400",
      created_shared: "bg-knauf-700",
      visible: "bg-white border-4 border-knauf-700",
    },
  };

  useEffect(() => {
    // On initial load and on filters/box change, we need to (re)fetch the pins
    debouncedFetch(filters, boundingBox, setData);
  }, [JSON.stringify(filters), boundingBox, refreshObservable]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-4 h-96 sm:h-full">
      <div className="flex-grow">
        <BlurredPlaceholder loading={loading}>
          <ScatterMap
            id="worksite-scatter-map"
            data={data}
            onClick={onClick}
            onNavigation={(viewState) => {
              const {width, height, zoom, latitude, longitude} = viewState;
              const bbox = getBoundingBox([longitude, latitude], zoom, width, height);
              setBoundingBox(bbox);
            }}
            draggable={true}
            deactivatedCategories={deactivatedCategories}
          />
        </BlurredPlaceholder>
      </div>
      <div className="flex flex-col gap-3 left-10">
        {SUPPORTED_BUSINESSES.map((business) => {
          if (business === "knauf" && !isKnaufVisibile) return null;
          return (
            <div key={business} className="flex flex-row gap-4 items-center">
              <p className="text-xxs uppercase text-am-500 font-medium w-16 text-right flex-shrink-0">
                {business}
              </p>
              <div className="flex flex-row gap-x-4 gap-y-1 flex-wrap">
                {SUPPORTED_CATEGORIES.map((category) => (
                  <LegendItem
                    key={`${business}-${category}`}
                    // @ts-ignore
                    color={pinColors[business][category]}
                    text={intl.formatMessage({id: `worksites_pins_${category}`})}
                    onClick={() => {
                      const key: string = `${business}-${category}`;
                      const newSet = new Set(deactivatedCategories);
                      if (deactivatedCategories.has(key)) {
                        newSet.delete(key);
                      } else {
                        newSet.add(key);
                      }
                      setDeactivatedCategories(newSet);
                    }}
                    active={!deactivatedCategories.has(`${business}-${category}`)}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}